import { Bitcoin, Activity, DollarSign, Zap } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { formatCurrency } from '../../utils/formatters';

/**
 * Stats Row Component
 * Displays the top stats cards: Price, RSI, Portfolio, Signal
 */
const StatsRow = ({
  currentPrice,
  rsi,
  portfolio,  // This should be displayPortfolio from parent
  signal,
  tradingMode,
  rsiColor,
  rsiStatus,
}) => {
  // Ensure portfolio has default values to prevent crashes
  const safePortfolio = portfolio || { total_value: 0, pnl: 0, pnl_percent: 0 };
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
      {/* BTC Price */}
      <Card className="matrix-card stat-card rounded-none" data-testid="price-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase text-[#008F11]">BTC/USD</span>
            <Bitcoin className="h-4 w-4 text-[#008F11]" strokeWidth={1.5} />
          </div>
          <p className="text-2xl md:text-3xl font-bold glow-text" data-testid="current-price">
            ${formatCurrency(currentPrice)}
          </p>
          <p className="text-[9px] uppercase tracking-widest text-[#008F11] opacity-60 mt-1" data-testid="price-source-label">
            Coinbase spot · may differ ~1% from other exchanges
          </p>
        </CardContent>
      </Card>

      {/* RSI */}
      <Card className="matrix-card stat-card rounded-none" data-testid="rsi-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase text-[#008F11]">RSI (14)</span>
            <Activity className="h-4 w-4 text-[#008F11]" strokeWidth={1.5} />
          </div>
          <p className="text-2xl md:text-3xl font-bold" style={{ color: rsiColor }} data-testid="rsi-value">
            {rsi.toFixed(1)}
          </p>
          <div className="mt-2">
            <div className="rsi-gauge">
              <div className="rsi-marker" style={{ left: `${rsi}%` }} />
            </div>
            <p className="text-xs mt-1" style={{ color: rsiColor }}>
              {rsiStatus}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Portfolio Value */}
      <Card className="matrix-card stat-card rounded-none" data-testid="portfolio-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase text-[#008F11]">
              {tradingMode === 'live' ? 'COINBASE PORTFOLIO' : 'PORTFOLIO'}
            </span>
            <DollarSign className="h-4 w-4 text-[#008F11]" strokeWidth={1.5} />
          </div>
          <p className="text-2xl md:text-3xl font-bold glow-text" data-testid="portfolio-value">
            ${formatCurrency(safePortfolio.total_value)}
          </p>
          <p className={`text-sm ${safePortfolio.pnl >= 0 ? 'number-positive' : 'number-negative'}`} data-testid="pnl-value">
            {safePortfolio.pnl >= 0 ? '+' : ''}${(safePortfolio.pnl || 0).toFixed(2)} ({(safePortfolio.pnl_percent || 0).toFixed(2)}%)
          </p>
          <div className="flex gap-2 mt-1 text-[10px]">
            <span className={safePortfolio.realized_pnl >= 0 ? 'text-[#00FF41]' : 'text-red-400'}>
              R: {safePortfolio.realized_pnl >= 0 ? '+' : ''}${(safePortfolio.realized_pnl || 0).toFixed(2)}
            </span>
            <span className={safePortfolio.unrealized_pnl >= 0 ? 'text-cyan-400' : 'text-orange-400'}>
              U: {safePortfolio.unrealized_pnl >= 0 ? '+' : ''}${(safePortfolio.unrealized_pnl || 0).toFixed(2)}
            </span>
          </div>
          {tradingMode === 'live' && (
            <p className="text-xs text-[#008F11] mt-1">LIVE COINBASE BALANCE</p>
          )}
        </CardContent>
      </Card>

      {/* Signal */}
      <Card className="matrix-card stat-card rounded-none" data-testid="signal-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase text-[#008F11]">SIGNAL</span>
            <Zap className="h-4 w-4 text-[#008F11]" strokeWidth={1.5} />
          </div>
          <p
            className={`text-2xl md:text-3xl font-bold ${
              signal === 'BUY' ? 'text-[#00FF41]' : signal === 'SELL' ? 'text-red-500' : 'text-[#F3F9D2]'
            }`}
            data-testid="signal-value"
          >
            {signal}
          </p>
          <p className="text-xs text-[#008F11] mt-1">
            {signal === 'BUY' ? 'RSI OVERSOLD' : signal === 'SELL' ? 'RSI OVERBOUGHT' : 'WAITING...'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default StatsRow;
